import { useCallback, useEffect, useRef, useState } from 'react';
import Select from 'ol/interaction/Select';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import type { Map as OlMap } from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import OlFeature from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import { MultiPolygon, Polygon } from 'ol/geom';
import { click, always } from 'ol/events/condition';
import booleanIntersects from '@turf/boolean-intersects';
import union from '@turf/union';
import { featureCollection } from '@turf/helpers';
import type {
  Feature as GeoJSONFeature,
  MultiPolygon as GeoJSONMultiPolygon,
  Polygon as GeoJSONPolygon,
} from 'geojson';
import { Tool } from 'contexts/@types/Map';

export type MergeError = 'not-touching' | 'not-polygon' | 'union-failed' | null;

export function useMergeFeatures(map: OlMap | undefined, selectedTool: Tool | null) {
  const [mergeLayer, setMergeLayer] = useState<VectorLayer<any> | null>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<OlFeature[]>([]);
  const [mergeError, setMergeError] = useState<MergeError>(null);
  const [isMerging, setIsMerging] = useState(false);

  const selectRef = useRef<Select | null>(null);
  const geoJsonFormat = new GeoJSON();

  type Coord = [number, number];

  function coordsEqual(a: Coord, b: Coord) {
    return Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9;
  }

  function segmentIntersection(p: Coord, p2: Coord, q: Coord, q2: Coord): Coord | null {
    const x1 = p[0], y1 = p[1];
    const x2 = p2[0], y2 = p2[1];
    const x3 = q[0], y3 = q[1];
    const x4 = q2[0], y4 = q2[1];

    const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
    if (Math.abs(denom) < 1e-12) return null;

    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

    if (ua >= -1e-10 && ua <= 1 + 1e-10 && ub >= -1e-10 && ub <= 1 + 1e-10) {
      return [x1 + ua * (x2 - x1), y1 + ua * (y2 - y1)];
    }
    return null;
  }

  function pointInPolygon(pt: Coord, ring: Coord[]): boolean {
    let inside = false;
    const n = ring.length - 1;
    let j = n - 1;
    for (let i = 0; i < n; i++) {
      const xi = ring[i][0], yi = ring[i][1];
      const xj = ring[j][0], yj = ring[j][1];
      if (((yi > pt[1]) !== (yj > pt[1])) &&
        pt[0] < (xj - xi) * (pt[1] - yi) / (yj - yi) + xi) {
        inside = !inside;
      }
      j = i;
    }
    return inside;
  }

  function pointOnSegment(pt: Coord, a: Coord, b: Coord): boolean {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-20) return coordsEqual(pt, a);

    const cross = dx * (pt[1] - a[1]) - dy * (pt[0] - a[0]);
    if ((cross * cross) / lenSq > 1e-9) return false;

    const t = (dx * (pt[0] - a[0]) + dy * (pt[1] - a[1])) / lenSq;
    return t >= -1e-9 && t <= 1 + 1e-9;
  }

  function pointOnRingBoundary(pt: Coord, ring: Coord[]): boolean {
    const n = ring.length - 1;
    for (let i = 0; i < n; i++) {
      if (pointOnSegment(pt, ring[i], ring[i + 1])) return true;
    }
    return false;
  }

  function ringsTouching(ringA: Coord[], ringB: Coord[]): boolean {
    const nA = ringA.length - 1;
    const nB = ringB.length - 1;

    for (let i = 0; i < nA; i++) {
      for (let j = 0; j < nB; j++) {
        if (segmentIntersection(ringA[i], ringA[i + 1], ringB[j], ringB[j + 1])) return true;
      }
    }
    for (const pa of ringA) {
      if (pointOnRingBoundary(pa, ringB) || pointInPolygon(pa, ringB)) return true;
    }
    for (const pb of ringB) {
      if (pointOnRingBoundary(pb, ringA) || pointInPolygon(pb, ringA)) return true;
    }
    return false;
  }

  function geometryToTurfFeature(
    geometry: Geometry
  ): GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon> | null {
    const geometryObject = geoJsonFormat.writeGeometryObject(geometry, {
      dataProjection: 'EPSG:3857',
      featureProjection: 'EPSG:3857',
    }) as GeoJSONPolygon | GeoJSONMultiPolygon | null;

    if (!geometryObject) return null;

    if (geometryObject.type !== 'Polygon' && geometryObject.type !== 'MultiPolygon') {
      return null;
    }

    return {
      type: 'Feature',
      properties: {},
      geometry: geometryObject,
    };
  }

  function geometriesConnected(geometries: Geometry[]): boolean {
    const n = geometries.length;
    if (n <= 1) return true;

    const turfGeometries = geometries.map((geometry) => geometryToTurfFeature(geometry));
    const adj: boolean[][] = Array.from({ length: n }, () => Array(n).fill(false));

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const first = turfGeometries[i];
        const second = turfGeometries[j];

        let touches = false;
        if (first && second) {
          touches = booleanIntersects(first, second);
        } else {
          const firstRings = getOuterRingsFromGeometry(geometries[i]);
          const secondRings = getOuterRingsFromGeometry(geometries[j]);
          touches = firstRings.some((ringA) =>
            secondRings.some((ringB) => ringsTouching(ringA, ringB))
          );
        }

        adj[i][j] = adj[j][i] = touches;
      }
    }

    const visited = new Set<number>([0]);
    const queue: number[] = [0];
    while (queue.length) {
      const cur = queue.shift()!;
      for (let j = 0; j < n; j++) {
        if (!visited.has(j) && adj[cur][j]) {
          visited.add(j);
          queue.push(j);
        }
      }
    }

    return visited.size === n;
  }

  function getOuterRingsFromGeometry(geometry: Geometry): Coord[][] {
    if (geometry instanceof Polygon) {
      const coordinates = geometry.getCoordinates();
      return coordinates[0] ? [coordinates[0] as Coord[]] : [];
    }

    if (geometry instanceof MultiPolygon) {
      return geometry
        .getCoordinates()
        .map((polygon) => polygon[0] as Coord[] | undefined)
        .filter((ring): ring is Coord[] => !!ring);
    }

    return [];
  }

  function unionAllGeometries(geometries: Geometry[]): Geometry | null {
    if (geometries.length === 0) return null;

    const turfPolygons = geometries
      .map((geometry) => geometryToTurfFeature(geometry))
      .filter((feature): feature is GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon> => !!feature);

    if (turfPolygons.length === 0) return null;

    const merged = union(
      featureCollection(
        turfPolygons
      )
    );

    if (!merged) return null;

    const geometry = geoJsonFormat.readGeometry(merged.geometry, {
      dataProjection: 'EPSG:3857',
      featureProjection: 'EPSG:3857',
    });

    if (geometry instanceof Polygon || geometry instanceof MultiPolygon) {
      return geometry;
    }

    return null;
  }

  const handleSetMergeLayer = (id: string) => {
    if (!map) return;

    if (id === 'disable') {
      setMergeLayer(null);
      setSelectedFeatures([]);
      setMergeError(null);
      return;
    }

    const layer = map
      .getLayers()
      .getArray()
      .find((l: any) => {
        const props = l.getProperties?.() ?? {};
        return (props.layertitle === id || props.uuid === id) && l instanceof VectorLayer;
      }) as VectorLayer<any> | undefined;

    if (layer) setMergeLayer(layer);
  };

  const removeInteractions = () => {
    try {
      if (selectRef.current && map) {
        selectRef.current.getFeatures().clear();
        map.removeInteraction(selectRef.current);
      }
    } catch (e) {}
    selectRef.current = null;
  };

  const mergeSelected = useCallback(() => {
    setMergeError(null);

    if (!mergeLayer || selectedFeatures.length < 2 || isMerging) return;

    const source = mergeLayer.getSource() as VectorSource | null;
    if (!source) return;

    setIsMerging(true);

    setTimeout(() => {
      try {
        const geometries: Geometry[] = [];

        for (const feat of selectedFeatures) {
          const geom = feat.getGeometry();
          if (!geom || (!(geom instanceof Polygon) && !(geom instanceof MultiPolygon))) {
            setMergeError('not-polygon');
            return;
          }

          const geometryRings = getOuterRingsFromGeometry(geom);
          if (geometryRings.length === 0) {
            setMergeError('not-polygon');
            return;
          }

          geometries.push(geom);
        }

        if (!geometriesConnected(geometries)) {
          setMergeError('not-touching');
          return;
        }

        const mergedGeometry = unionAllGeometries(geometries);
        if (!mergedGeometry) {
          setMergeError('union-failed');
          return;
        }

        const mergedFeature = new OlFeature({ geometry: mergedGeometry });

        const props = { ...selectedFeatures[0].getProperties() };
        delete props.geometry;
        mergedFeature.setProperties(props);

        selectedFeatures.forEach((f) => source.removeFeature(f));
        source.addFeature(mergedFeature);

        if (selectRef.current) selectRef.current.getFeatures().clear();

        const firstFeature = selectedFeatures[0];
        const style = firstFeature.getStyle();

        if (style) {
          mergedFeature.setStyle(style);
        }
        setSelectedFeatures([]);
      } catch (err) {
        console.error('[useMergeFeatures] erro no merge:', err);
        setMergeError('union-failed');
      } finally {
        setIsMerging(false);
      }
    }, 0);
  }, [isMerging, mergeLayer, selectedFeatures]);


  useEffect(() => {
    if (!map) return;

    const isMergeTool = String(selectedTool?.action) === 'merge';
    if (!isMergeTool || !mergeLayer) {
      removeInteractions();
      setSelectedFeatures([]);
      setMergeError(null);
      return;
    }

    const select = new Select({
      condition: click,
      toggleCondition: always,
      layers: [mergeLayer],
    });

    selectRef.current = select;

    select.on('select', () => {
      const collection = select.getFeatures();
      while (collection.getLength() > 2) {
        collection.removeAt(0);
      }
      setSelectedFeatures(collection.getArray().slice());
      setMergeError(null);
    });

    map.addInteraction(select);

    return () => {
      removeInteractions();
    };
  }, [map, mergeLayer, selectedTool?.action]);

  useEffect(() => {
    return () => removeInteractions();
  }, []);

  return {
    mergeLayer,
    handleSetMergeLayer,
    selectedFeatures,
    mergeError,
    isMerging,
    mergeSelected,
  };
}
